import { contratosFetch } from '@/pantallas/SucursalVirtual/Contratos/contratosFetch';
import {
  DatosContrato,
  FiltrarContrato,
  EtiquetasFiltrosContratos,
  FiltrosGrilla,
} from '@/pantallas/SucursalVirtual/Contratos/types';
import moment from 'moment';

class Contrato {
  static async crearContrato(
    contrato: DatosContrato,
    usuarioId: string,
    clienteDefecto: string
  ) {
    const body = {
      ...contrato,
      usuarioId,
      clienteDefecto,
    };

    try {
      const { data } = await contratosFetch({
        endpoint: 'crear_contrato',
        body,
      });

      const { MENSAJE_RESPUESTA, CODIGO_RESPUESTA, ID_CONTRATO } = data;

      if (CODIGO_RESPUESTA !== '1') {
        throw new Error(MENSAJE_RESPUESTA);
      }
      return {
        mensajeExito: MENSAJE_RESPUESTA,
      };
    } catch (e) {
      const mensajeError =
        e instanceof Error
          ? e.message
          : 'Ha ocurrido un error al crear el contrato';
      throw new Error(mensajeError);
    }
  }

  static async obtenerContratosPorFiltros(filtrosGrilla: FiltrosGrilla) {
    let fechaDesdeStr = '';
    let fechaHastaStr = '';

    if (filtrosGrilla.fechaDesde) {
      fechaDesdeStr = filtrosGrilla.fechaDesde.format('YYYY-MM-DD');
    }
    if (filtrosGrilla.fechaHasta) {
      fechaHastaStr = filtrosGrilla.fechaHasta.format('YYYY-MM-DD');
    }

    const body: FiltrarContrato = {
      ...filtrosGrilla,
      fechaDesde: fechaDesdeStr,
      fechaHasta: fechaHastaStr,
    };

    try {
      const { data } = await contratosFetch({
        endpoint: 'obtener_contratos_por_filtros',
        body,
      });
      if (!data) {
        throw new Error(
          'No se ha podido obtener la información de los contratos'
        );
      }

      const {
        MENSAJE_RESPUESTA,
        CODIGO_RESPUESTA,
        CONTRATOS,
        TOTAL_REGISTROS,
      } = data;

      if (CODIGO_RESPUESTA !== '1') {
        throw new Error(MENSAJE_RESPUESTA);
      }
      return {
        contratos: CONTRATOS,
        totalRegistros: TOTAL_REGISTROS,
      };
    } catch (error) {
      const mensajeError =
        error instanceof Error
          ? error.message
          : 'Ha ocurrido un error al obtener los contratos';
      throw new Error(mensajeError);
    }
  }

  static async obtenerFiltrosContratos(
    filtrosDeseados: EtiquetasFiltrosContratos = []
  ) {
    try {
      const { data } = await contratosFetch({
        endpoint: 'obtener_filtros_contratos',
        body: { filtrosDeseados },
      });

      if (!data) {
        throw new Error(
          'No se ha podido obtener la información de los filtros'
        );
      }

      const {
        MENSAJE_RESPUESTA,
        CODIGO_RESPUESTA,
        EJECUTIVOS,
        CLIENTES,
        ESTADOS_FIRMA,
        TIPOS_FECHAS,
      } = data;
      if (CODIGO_RESPUESTA !== '1') {
        throw new Error(MENSAJE_RESPUESTA);
      }

      return {
        ejecutivos: EJECUTIVOS,
        clientes: CLIENTES,
        estadosFirma: ESTADOS_FIRMA,
        tiposFechas: TIPOS_FECHAS,
      };
    } catch (error) {
      const mensajeError =
        error instanceof Error
          ? error.message
          : 'Ha ocurrido un error al obtener los filtros';
      throw new Error(mensajeError);
    }
  }

  static async obtenerContratoPorId(contratoId: string) {
    try {
      const { data } = await contratosFetch({
        endpoint: 'obtener_contrato_por_id',
        body: { contratoId },
      });

      if (!data) {
        throw new Error('No se ha podido obtener la información del contrato');
      }
      const { MENSAJE_RESPUESTA, CODIGO_RESPUESTA, CONTRATO } = data;

      if (CODIGO_RESPUESTA !== '1') {
        throw new Error(MENSAJE_RESPUESTA);
      }

      const contrato: DatosContrato = {
        rut: CONTRATO.rut,
        cliente: CONTRATO.cliente,
        ejecutivoEmail: CONTRATO.ejecutivo_email,
        ejecutivoId: CONTRATO.id_ejecutivo,
        areaId: CONTRATO.id_area,
        estadoFirmaId: CONTRATO.id_estado_firma,
        contratoId: CONTRATO.id_contrato,
        fechaCreacion: CONTRATO.fecha_creacion
          ? moment(CONTRATO.fecha_creacion.substring(0, 10))
          : null,
      };
      return { contrato };
    } catch (e) {
      const mensajeError =
        e instanceof Error
          ? e.message
          : 'Ha ocurrido un error al obtener el contrato';
      throw new Error(mensajeError);
    }
  }

  static async obtenerEjecutivoPorId(email: FiltrarContrato['email']) {
    try {
      const { data } = await contratosFetch({
        endpoint: 'obtener_datos_ejecutivo',
        body: { email },
      });

      if (!data) {
        throw new Error('No se ha podido obtener la información del usuario');
      }
      const { MENSAJE_RESPUESTA, CODIGO_RESPUESTA, EJECUTIVO } = data;
      if (CODIGO_RESPUESTA !== '1') {
        throw new Error(MENSAJE_RESPUESTA);
      }
      return EJECUTIVO;
    } catch (error) {
      const mensajeError =
        error instanceof Error
          ? error.message
          : 'Ha ocurrido un error al obtener el usuario';
      throw new Error(mensajeError);
    }
  }

  static async obtenerContratoId() {
    try {
      const { data } = await contratosFetch({
        endpoint: 'obtener_id_contrato',
      });

      const { MENSAJE_RESPUESTA, CODIGO_RESPUESTA, ID_CONTRATO } = data;

      if (CODIGO_RESPUESTA !== '1') {
        throw new Error(MENSAJE_RESPUESTA);
      }

      return {
        contratoId: ID_CONTRATO,
      };
    } catch (error) {
      const mensajeError =
        error instanceof Error
          ? error.message
          : 'Ha ocurrido un error al obtener el id del contrato';
      throw new Error(mensajeError);
    }
  }

  static async editarContrato(
    contrato: DatosContrato,
    usuarioId: string,
    clienteDefecto: string
  ) {
    const body = {
      ...contrato,
      usuarioId,
      clienteDefecto,
    };

    try {
      const { data } = await contratosFetch({
        endpoint: 'editar_contrato',
        body,
      });

      if (!data) {
        throw new Error('No se ha podido editar el contrato');
      }
      const { MENSAJE_RESPUESTA, CODIGO_RESPUESTA } = data;
      if (CODIGO_RESPUESTA !== '1') {
        throw new Error(MENSAJE_RESPUESTA);
      }
      return { mensajeExito: MENSAJE_RESPUESTA };
    } catch (e) {
      const mensajeError =
        e instanceof Error
          ? e.message
          : 'Ha ocurrido un error al editar el contrato';
      throw new Error(mensajeError);
    }
  }

  static async eliminarContrato(
    contratoId: string,
    usuarioId: string,
    clienteDefecto: string
  ) {
    try {
      const { data } = await contratosFetch({
        endpoint: 'eliminar_contrato',
        body: { contratoId, usuarioId, clienteDefecto },
      });

      if (!data) {
        throw new Error('No se ha podido eliminar el contrato');
      }

      const { MENSAJE_RESPUESTA, CODIGO_RESPUESTA } = data;

      if (CODIGO_RESPUESTA !== '1') {
        throw new Error(MENSAJE_RESPUESTA);
      }
      return { mensajeExito: MENSAJE_RESPUESTA };
    } catch (e) {
      const mensajeError =
        e instanceof Error
          ? e.message
          : 'Ha ocurrido un error al eliminar el contrato';
      throw new Error(mensajeError);
    }
  }

  static async obtenerExcelContratos(
    filtrosGrilla: FiltrosGrilla,
    usuarioId: string,
    clienteDefecto: string
  ) {
    let fechaDesdeStr = '';
    let fechaHastaStr = '';

    if (filtrosGrilla.fechaDesde) {
      fechaDesdeStr = filtrosGrilla.fechaDesde.format('YYYY-MM-DD');
    }
    if (filtrosGrilla.fechaHasta) {
      fechaHastaStr = filtrosGrilla.fechaHasta.format('YYYY-MM-DD');
    }

    const body = {
      ...filtrosGrilla,
      fechaDesde: fechaDesdeStr,
      fechaHasta: fechaHastaStr,
      usuarioId,
      clienteDefecto,
    };

    try {
      const { data } = await contratosFetch({
        endpoint: 'obtener_excel_contratos',
        body,
      });

      if (!data) {
        throw new Error('No se ha podido obtener el archivo csv');
      }

      const { MENSAJE_RESPUESTA, CODIGO_RESPUESTA } = data;

      if (CODIGO_RESPUESTA !== '1') {
        throw new Error(MENSAJE_RESPUESTA);
      }
      return { mensajeExito: MENSAJE_RESPUESTA };
    } catch (e) {
      const mensajeError =
        e instanceof Error
          ? e.message
          : 'Ha ocurrido un error al obtener el archivo csv';
      throw new Error(mensajeError);
    }
  }
}

export default Contrato;
